import React from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useEntityDoc, useEntityList } from '../../data/entities';
import WorkspacePipelines from '../../components/WorkspacePipelines';
import WorkspaceWorkloads from '../../components/WorkspaceWorkloads';
import WorkspaceDatasets from '../../components/WorkspaceDatasets';
import WorkspaceDevSpaces from '../../components/WorkspaceDevSpaces';

// Project context shell: the sidebar's active project renders here — header
// with the workspace breadcrumb, module pages in the outlet. Working entities
// (pipelines, workloads, datasets, dev spaces) are workspace-scoped, so a
// project view shows its parent workspace's data for each module.
export default function ProjectShell() {
  const { org, myRole } = useOutletContext();
  const { workspaceId, projectId } = useParams();
  const { doc: ws } = useEntityDoc('workspace', { org: org.id }, workspaceId);
  const { doc: project } = useEntityDoc('project', { org: org.id, workspace: workspaceId }, projectId);

  if (ws === undefined || project === undefined) {
    return <p className="cs-help">Loading…</p>;
  }
  if (ws === null || project === null) {
    return (
      <p className="cs-help">
        Project not found. <Link to={`/orgs/${org.id}`}>Back to overview</Link>
      </p>
    );
  }

  return (
    <div>
      <p className="cs-help" style={{ margin: 0, fontSize: 12 }}>
        <Link to={`/orgs/${org.id}`} style={{ color: 'inherit' }}>{org.name}</Link>
        {' / '}
        <Link to={`/orgs/${org.id}/ws/${workspaceId}`} style={{ color: 'inherit' }}>{ws.name}</Link>
      </p>
      <h1 style={{ margin: '6px 0 0' }}>{project.name}</h1>
      {project.description && (
        <p className="cs-help" style={{ marginTop: 6 }}>{project.description}</p>
      )}
      <Outlet context={{ org, myRole, workspaceId, projectId, ws, project }} />
    </div>
  );
}

// ---- Project module pages (thin wrappers over the workspace components) ----

export function ProjectOverview() {
  const { org, workspaceId, projectId } = useOutletContext();
  const scope = { org: org.id, workspace: workspaceId };
  const { docs: pipelines } = useEntityList('pipeline', scope);
  const { docs: workloads } = useEntityList('workload', scope);
  const { docs: datasets } = useEntityList('dataset', scope);
  const { docs: spaces } = useEntityList('devSpace', scope);
  const base = `/orgs/${org.id}/p/${workspaceId}/${projectId}`;

  const activeWorkloads = (workloads || []).filter((w) =>
    ['requested', 'launching', 'running'].includes(w.status)).length;
  const runningSpaces = (spaces || []).filter((s) =>
    ['requested', 'launching', 'running'].includes(s.status)).length;

  const cards = [
    {
      to: `${base}/nqube`, label: 'nQube', tag: 'Workflow Builder',
      stat: count(pipelines, 'pipeline'),
    },
    {
      to: `${base}/cloudops`, label: 'DevOps', tag: 'Compute workloads',
      stat: workloads == null ? '…' : `${count(workloads, 'workload')}${activeWorkloads ? ` · ${activeWorkloads} active` : ''}`,
    },
    {
      to: `${base}/devstudio`, label: 'Dev Studio', tag: 'Browser VS Code',
      stat: spaces == null ? '…' : `${count(spaces, 'space')}${runningSpaces ? ` · ${runningSpaces} running` : ''}`,
    },
    {
      to: `${base}/listen`, label: 'Listen', tag: 'Datasets on shared storage',
      stat: count(datasets, 'dataset'),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 20, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
      {cards.map((c) => (
        <Link key={c.to} to={c.to} className="cs-card" style={{ padding: 16, textDecoration: 'none' }}>
          <strong style={{ color: 'var(--text)', fontSize: 16 }}>{c.label}</strong>
          <p className="cs-help" style={{ margin: '4px 0 10px', fontSize: 12 }}>{c.tag}</p>
          <span className="cs-help" style={{ fontSize: 13 }}>{c.stat}</span>
        </Link>
      ))}
    </div>
  );
}

function count(docs, unit) {
  if (docs == null) return '…';
  return `${docs.length} ${unit}${docs.length === 1 ? '' : 's'}`;
}

export function ProjectNQube() {
  const { org, workspaceId } = useOutletContext();
  return <WorkspacePipelines orgId={org.id} workspaceId={workspaceId} />;
}

export function ProjectCloudOps() {
  const { org, workspaceId } = useOutletContext();
  return <WorkspaceWorkloads orgId={org.id} workspaceId={workspaceId} />;
}

export function ProjectDevStudio() {
  const { org, workspaceId } = useOutletContext();
  return <WorkspaceDevSpaces orgId={org.id} workspaceId={workspaceId} />;
}

export function ProjectListen() {
  const { org, workspaceId } = useOutletContext();
  return <WorkspaceDatasets orgId={org.id} workspaceId={workspaceId} />;
}
